import {
	BaseUnsavedChangesPrompt,
	Drawer,
	DrawerHeader,
	DrawerHeading,
	DrawerProps,
	Id,
	useDrawerUnsavedChangesPrompt,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AddressesForm, AddressesFormValues, emptyAddressesFormValues } from "../../companies/create/AddressesForm";
import { CreateCompanyStepper } from "../../companies/create/CreateCompanyStepper";
import { companyService } from "../../shared/api/companyService";
import {
	CarFleetStatus,
	CompanyCreateAddressRequest,
	CompanyCreateMasterDataRequest,
	CompanyCreateRequest,
	CompanyCreateTagRequest,
	CompanyTenantPivotValue,
} from "../../shared/types";
import { useGlobalTenants } from "../../tenants/global/GlobalTenantProvider";
import { MasterDataForm, MasterDataFormValues, emptyMasterDataFormValues } from "./MasterDataForm";
import { TagsForm, TagsFormValues, emptyTagsFormValues } from "./TagsForm";

export type CreateCompanyStep = "masterData" | "addresses" | "tags";
export const steps: CreateCompanyStep[] = ["masterData", "addresses", "tags"];

interface CreateCompanyFormValues {
	masterData: MasterDataFormValues;
	addresses: AddressesFormValues;
	tags: TagsFormValues;
}

const emptyCreateCompanyFormValues: CreateCompanyFormValues = {
	masterData: emptyMasterDataFormValues,
	addresses: emptyAddressesFormValues,
	tags: emptyTagsFormValues,
};

const isStepDone = (step: CreateCompanyStep, activeStep: CreateCompanyStep) =>
	steps.indexOf(activeStep) >= steps.indexOf(step);

const mapFormValuesToRequest = (submittingStep: CreateCompanyStep, values: CreateCompanyFormValues) => {
	const masterDataRequest: CompanyCreateMasterDataRequest = {
		name: values.masterData.name,
		parentId: values.masterData.parent?.id as Id,
		pivotValues: values.masterData.pivotValues,
	};

	let addressesRequest: CompanyCreateAddressRequest[] = [];
	if (isStepDone("addresses", submittingStep)) {
		addressesRequest = values.addresses.addresses.map((addr) => ({
			city: addr.city,
			countryId: addr.country?.id as Id,
			zip: addr.zip,
			street: addr.street,
			additionalInfo: addr.additionalInfo,
			houseNumber: addr.houseNumber,
			isPrimary: addr.isPrimary,
		}));
	}

	let tagsRequest: CompanyCreateTagRequest[] = [];
	if (isStepDone("tags", submittingStep)) {
		tagsRequest = values.tags.tags.map((t) => t.id);
	}

	const request: CompanyCreateRequest = {
		masterData: masterDataRequest,
		addresses: addressesRequest,
		tagIds: tagsRequest,
	};

	return request;
};

interface CreateCompanyDrawerContentProps {
	name?: string;
	onSubmit: (values: CompanyCreateRequest) => Promise<void>;
	setIsDirty?: (dirty: boolean) => void;
}

const WideDrawer = styled(Drawer)`
	width: 600px;
`;

export const emptyCompanyPivotValue: CompanyTenantPivotValue = {
	eventPriority: null,
	carFleetStatus: CarFleetStatus.NOT_RELEVANT,
};

export const CreateCompanyDrawerContent: FC<CreateCompanyDrawerContentProps> = ({ onSubmit, setIsDirty, name }) => {
	const { selected } = useGlobalTenants();
	const [[step, formValues], setFormValues] = useState<[CreateCompanyStep, CreateCompanyFormValues]>([
		"masterData",
		{
			...emptyCreateCompanyFormValues,
			masterData: {
				...emptyCreateCompanyFormValues.masterData,
				name: name || emptyCreateCompanyFormValues.masterData.name,
				tenants: selected.tenant ? [selected.tenant] : [],
				pivotValues: selected.tenant ? { [selected.tenant.id]: emptyCompanyPivotValue } : {},
			},
		},
	]);

	const handleGoBack = (step: Exclude<CreateCompanyStep, "masterData">) => {
		setFormValues(([, values]) => [steps[steps.indexOf(step) - 1], values]);
	};

	const handleSubmit = (step: CreateCompanyStep, formValues: CreateCompanyFormValues) => {
		setIsDirty && setIsDirty(true);
		setFormValues([step, formValues]);
	};

	const handleSave = async (submittingStep: CreateCompanyStep, values: CreateCompanyFormValues) => {
		const request = mapFormValuesToRequest(submittingStep, values);
		await onSubmit(request);
	};

	switch (step) {
		case "masterData":
			return (
				<>
					<CreateCompanyStepper activeStep={step} />
					<MasterDataForm
						setIsDirty={setIsDirty}
						initialValues={formValues.masterData}
						onSubmit={(values) => handleSubmit("addresses", { ...formValues, masterData: values })}
					/>
				</>
			);
		case "addresses":
			return (
				<>
					<CreateCompanyStepper activeStep={step} />
					<AddressesForm
						initialValues={formValues.addresses}
						onGoBack={() => handleGoBack("addresses")}
						onSubmit={async (values) => {
							const nextValues = { ...formValues, addresses: values };
							switch (values.action) {
								case "save":
									await handleSave("addresses", nextValues);
									break;
								case "next":
									handleSubmit("tags", nextValues);
									break;
							}
						}}
					/>
				</>
			);
		case "tags":
			return (
				<>
					<CreateCompanyStepper activeStep={step} />
					<TagsForm
						initialValues={formValues.tags}
						onGoBack={() => handleGoBack("tags")}
						onSubmit={(values) => handleSave("tags", { ...formValues, tags: values })}
					/>
				</>
			);
	}
};

interface Props extends DrawerProps {
	onSubmit: (values: CompanyCreateRequest) => Promise<boolean>;
	onClose: () => void;
}

export const CreateCompanyDrawer: FC<Props> = ({ onSubmit, onClose, ...drawerProps }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { handleClose, setIsDirty, ...promptProps } = useDrawerUnsavedChangesPrompt(onClose);

	const handleSubmit = async (request: CompanyCreateRequest) => {
		if (await onSubmit(request)) {
			onClose();
		}
	};

	const handleClosed = () => {
		void queryClient.invalidateQueries({
			queryKey: [...companyService.indexKey],
			refetchType: "none",
		});
	};

	return (
		<>
			<BaseUnsavedChangesPrompt {...promptProps} />
			<WideDrawer {...drawerProps} onClose={handleClose} onClosed={handleClosed}>
				<DrawerHeader onClose={handleClose}>
					<DrawerHeading>{t("Add company")}</DrawerHeading>
				</DrawerHeader>
				<CreateCompanyDrawerContent onSubmit={handleSubmit} setIsDirty={setIsDirty} />
			</WideDrawer>
		</>
	);
};
